import React, { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Button, Container, Box,
    IconButton, Drawer, List, ListItem, ListItemText, ListItemIcon, Divider,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import useAuth from '../../hooks/useAuth';

export default function PublicLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/');
        setMobileOpen(false);
    };

    const dashPath = user?.role === 'trainer' ? '/trainer/candidatures' :
        user?.role === 'director' ? '/director/offres' : '/admin/offres';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppBar position="sticky" sx={{
                background: 'linear-gradient(90deg, #1b263b 0%, #415a77 100%)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}>
                <Toolbar sx={{ height: { xs: 64, sm: 80 }, px: { xs: 2, sm: 3 } }}>
                    <HomeIcon sx={{ mr: 1.5, fontSize: { xs: 24, sm: 28 } }} />
                    <Typography
                        variant="h5"
                        component={Link}
                        to="/"
                        sx={{
                            flexGrow: 1,
                            textDecoration: 'none',
                            color: 'white',
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            fontSize: { xs: '1.2rem', sm: '1.5rem' },
                        }}
                    >
                        OFPPT
                    </Typography>

                    {/* Desktop nav */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                        {user ? (
                            <>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
                                        {user.prenom} {user.nom}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'capitalize' }}>
                                        {user.role}
                                    </Typography>
                                </Box>
                                <Button
                                    color="inherit"
                                    onClick={() => navigate(dashPath)}
                                    size="medium"
                                    variant="outlined"
                                    sx={{
                                        borderColor: 'rgba(255,255,255,0.3)',
                                        color: 'white',
                                        textTransform: 'none',
                                        borderRadius: 2.5,
                                        px: 3,
                                        '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                                    }}
                                >
                                    Mon espace
                                </Button>
                                <Button
                                    color="inherit"
                                    onClick={handleLogout}
                                    size="small"
                                    sx={{ textTransform: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}
                                >
                                    Déconnexion
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    component={Link}
                                    to="/login"
                                    color="inherit"
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Connexion
                                </Button>
                                <Button
                                    component={Link}
                                    to="/register"
                                    variant="contained"
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#1b263b',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderRadius: 2.5,
                                        px: 4,
                                        '&:hover': { bgcolor: '#f8fafc', transform: 'translateY(-2px)' },
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}
                                >
                                    Créer un compte
                                </Button>
                            </>
                        )}
                    </Box>

                    {/* Mobile hamburger */}
                    <IconButton
                        color="inherit"
                        onClick={() => setMobileOpen(true)}
                        sx={{ display: { xs: 'flex', md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { width: 280, borderRadius: '16px 0 0 16px' },
                }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={800} color="primary">OFPPT</Typography>
                    <IconButton onClick={() => setMobileOpen(false)}><CloseIcon /></IconButton>
                </Box>
                <Divider />
                {user ? (
                    <List>
                        <ListItem sx={{ py: 2, px: 3 }}>
                            <Box>
                                <Typography variant="body1" fontWeight={700}>{user.prenom} {user.nom}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{user.role}</Typography>
                            </Box>
                        </ListItem>
                        <Divider />
                        <ListItem component={Link} to={dashPath} onClick={() => setMobileOpen(false)}
                            sx={{ py: 1.5, color: 'inherit', textDecoration: 'none' }}>
                            <ListItemIcon><DashboardIcon /></ListItemIcon>
                            <ListItemText primary="Mon espace" />
                        </ListItem>
                        <ListItem onClick={handleLogout} sx={{ py: 1.5, cursor: 'pointer' }}>
                            <ListItemIcon><LogoutIcon /></ListItemIcon>
                            <ListItemText primary="Déconnexion" />
                        </ListItem>
                    </List>
                ) : (
                    <List>
                        <ListItem component={Link} to="/login" onClick={() => setMobileOpen(false)}
                            sx={{ py: 1.5, color: 'inherit', textDecoration: 'none' }}>
                            <ListItemIcon><LoginIcon /></ListItemIcon>
                            <ListItemText primary="Connexion" />
                        </ListItem>
                        <ListItem component={Link} to="/register" onClick={() => setMobileOpen(false)}
                            sx={{ py: 1.5, color: 'inherit', textDecoration: 'none' }}>
                            <ListItemIcon><PersonAddIcon /></ListItemIcon>
                            <ListItemText primary="Créer un compte" />
                        </ListItem>
                    </List>
                )}
            </Drawer>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 6 }, px: { xs: 2, sm: 3 }, flexGrow: 1 }}>
                <Outlet />
            </Container>

            <Box component="footer" sx={{
                py: { xs: 4, sm: 6 },
                textAlign: 'center',
                bgcolor: '#0d1b2a',
                color: 'rgba(255,255,255,0.5)',
                borderTop: '1px solid rgba(255,255,255,0.05)',
                px: 2,
            }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 1, fontWeight: 700 }}>
                    IOFPPT
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Système de Gestion des Formateurs Vacataires
                </Typography>
                <Typography variant="caption" display="block">
                    © 2026 OFPPT. Tous droits réservés.
                </Typography>
            </Box>
        </Box>

    );
}
